import { AIOptionsFlowType, AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { AddCard, AddPhotoAlternate } from '@mui/icons-material'
import { Box, useMediaQuery } from '@mui/material'
import { CurrentActionType, ToolbarMode, useClientGallery } from '../_main/contexts'
import { FC, Fragment, useCallback, useEffect, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { decimalFromBig, formatPrice } from 'utils/price'
import { useGalleryAdditionalVisuals, useGalleryAssignment, useGalleryDeal, useGalleryProduct, useGalleryVisualSelection, useGalleryVisualsDownloadArchive, useGalleryVisualsMeta } from '../../contexts'
import { useLocation, useParams } from 'react-router'

import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { GalleryFloatingToolBar } from 'components/common/Gallery/GalleryFloatingToolBar'
import { GRAY_700, MOBILE_VIEW_WIDTH_QUERY } from 'constants/styling/theme'
import { MUIButton } from 'components/common/MUIButton'
import { MUITooltip } from 'components/common/MUITooltip'
import { PIPEDRIVE_INFINITY } from 'constants/pipedrive'
import { Path } from 'constants/router'
import { RealEstatePropertyDTO } from 'models/property'
import { countPhotos } from '../../blurPairs'
import { externalNavigate } from 'utils/helpers'
import { getPricingUrl } from 'utils/localization'
import { getPropertyPathname } from 'utils/helpers/getPropertyPathname'
import { identifiableProduct } from 'utils/serialization/productIdentifier'
import i18n from 'translations/i18n'
import { invalidateListVisuals } from 'dataQueries/visual.query'
import { logger } from 'utils/logging'
import { useActionPopup, useWorkspacePlan } from 'utils/hooks'
import { useAuth0 } from 'utils/auth'
import { useCanUserOrder } from 'utils/user/canUserOrder'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useSelectVisuals } from 'dataQueries'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * @interface Props for the ClientGalleryFloatingToolBar component.
 */
interface Props {
  /** Property data for property flow - optional for gallery flow */
  propertyData?: RealEstatePropertyDTO
}
/**
 * Floating tool bar for Client Gallery.
 * @example <ClientGalleryFloatingToolBar propertyData={propertyData} />
 */
export const ClientGalleryFloatingToolBar: FC<Props> = ({ propertyData }) => {
  const { t } = useTranslation(['gallery', 'gallery_staging_flow'])
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { id } = useParams<{ id: string }>()
  const isMobileView = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)

  const canUserOrder = useCanUserOrder()
  const { initiateArchiveDownload } = useGalleryVisualsDownloadArchive()
  const { userPropertyWorkspaceId, currentUserWorkspace } = useUserData()
  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()
  const {
    mode,
    assignmentId,
    isToolBarOpen,
    setIsToolBarOpen,
    setCurrentActionType
  } = useClientGallery()
  const {
    prepurchasedVisualsTotal,
    prepurchasedVisualsRemaining,
    purchasedVisualsExist,
    purchasedVisualsKeys,
    unPurchasedVisualsKeys,
    allVisualsFilenames,
    blurPartners,
  } = useGalleryVisualsMeta()
  const {
    pricePerAdditionalVisual,
    selectedAdditionalVisualsCount,
    totalForAdditionalVisualsAfterDiscount,
    selectedPaidVisualsCount,
    selectedFreeVisualsCount,
    currency,
    isAdditionalVisualPurchaseDisabled,
    setIsAdditionalVisualsPurchaseModalOpen,
  } = useGalleryAdditionalVisuals()
  const {
    selected,
    selectedPurchasedVisualsCount,
    selectedNotPurchasedVisualsCount,
    selectedNotPurchasedVisualsKeys,
    selectAllPurchased,
    selectAllUnpurchased,
    deselectAll,
  } = useGalleryVisualSelection()
  const { isVideo, isSelfUploadProduct } = useGalleryProduct()
  const { logGalleryEvent, product } = useGalleryAssignment()
  const { dealData } = useGalleryDeal()
  const { showConfirm } = useActionPopup()
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()

  const { roles } = useAuth0()
  const selectVisualsMutation = useSelectVisuals()

  const isPropertyPathname = useMemo(() => getPropertyPathname(pathname), [pathname])

  const allowSelectAllButton = useMemo(() => {
    if (countPhotos(unPurchasedVisualsKeys, blurPartners) > prepurchasedVisualsRemaining) return !isAdditionalVisualPurchaseDisabled
    return true
  }, [isAdditionalVisualPurchaseDisabled, prepurchasedVisualsRemaining, unPurchasedVisualsKeys, blurPartners])

  /** Disabled visuals editing for subscription clients with no purchased visuals exist and no selected visuals */
  const isVisualsEditingDisabled = useMemo(() => hasWorkspaceActiveSubscription && (!purchasedVisualsExist || !selected.size), [hasWorkspaceActiveSubscription, purchasedVisualsExist, selected.size])
  /** Disabled "Pay and add to purchased" button when user cannot order and has selected unpaid visuals */
  const isPayAndAddDisabled = useMemo(() => !canUserOrder && selectedPaidVisualsCount > 0, [canUserOrder, selectedPaidVisualsCount])
  const totalExtraPrice = formatPrice(decimalFromBig(totalForAdditionalVisualsAfterDiscount))
  const selectionModeLabel = useMemo(() => {

    const selectedValue = Math.min(selectedNotPurchasedVisualsCount, prepurchasedVisualsRemaining)
    const isShowExtraValue = selectedPaidVisualsCount > 0 && pricePerAdditionalVisual
    const extraValue = isShowExtraValue ? t('selected_n_extra', { plus: '+', count: selectedPaidVisualsCount, totalPrice: currency ? `(${totalExtraPrice}${currency})` : null }) : ''
    const selectedValueWithMaxValue = `${selectedValue}/${prepurchasedVisualsRemaining}`

    if (isSelfUploadProduct) {
      return (
        <Fragment>{selectedValue} {t('selected_visuals')}</Fragment>
      )
    }

    return (
      <Fragment>
        {prepurchasedVisualsRemaining > 0 && <>{selectedValueWithMaxValue} {purchasedVisualsExist ? 'extra' : ''} <Box fontWeight={400} display='inline'>{t('prepaid_visuals')}</Box></>}
        {!isMobileView && !!extraValue && <span>{extraValue}</span>}
        {isMobileView && <Box fontWeight={400} fontSize={12} display='flex'>{extraValue}</Box>}
        {blurPartners.size > 0 && <Box fontWeight={400} display='inline' color={GRAY_700}> · {t('gallery:blur_pair_toolbar_hint')}</Box>}
      </Fragment>
    )
  }, [selectedNotPurchasedVisualsCount, prepurchasedVisualsRemaining, selectedPaidVisualsCount, pricePerAdditionalVisual, t, currency, totalExtraPrice, isSelfUploadProduct, purchasedVisualsExist, isMobileView, blurPartners])

  const handleVisualsSelection = useCallback(async () => {
    if (!product) return
    if (!selectedFreeVisualsCount && !selectedPaidVisualsCount) return

    // The selection wire identifies the assignment product by its Product2 code alone, so a product
    // without one cannot be selected at all — say so rather than fail the request silently.
    const assignmentProduct = identifiableProduct(product.productCode)

    if (!assignmentProduct) {
      spawnErrorToast(t('gallery_staging_flow:upsell_abort_error'))
      logger.error('Visuals not selected - the assignment product carries no product code', undefined, {
        context: { component: 'ClientGalleryFloatingToolBar', action: 'handleVisualsSelection', metadata: { assignmentId, productId: product.id } },
      })

      return
    }

    logGalleryEvent(AnalyticsEvent.CLICK_CONFIRM_VISUAL_SELECTION, {
      assignmentOwnerId: dealData?.customer.id,
      assignmentOwnerEmail: dealData?.customer.email,
      productKind: product?.kind,
      numberOfVisuals: selectedNotPurchasedVisualsCount,
      numberOfFiles: selectedNotPurchasedVisualsKeys.size,
      blurPairCountingActive: blurPartners.size > 0,
    })

    if (product?.isAiPhotoEditing) {
      logGalleryEvent(AnalyticsEvent.PURCHASE_AI_VISUALS_IN_GALLERY, {
        amountofVisualsDelivered: allVisualsFilenames.length,
        amountofVisualsOrdered: selectedNotPurchasedVisualsCount,
        numberOfFiles: selectedNotPurchasedVisualsKeys.size,
        blurPairCountingActive: blurPartners.size > 0,
        visualIDs: Array.from(selectedNotPurchasedVisualsKeys),
        assignmentId: product?.id,
        flow: isSelfUploadProduct ? AIOptionsFlowType.GALLERY : AIOptionsFlowType.PF,
      })
    }

    // Special case for unlimited selection -> mark all as purchased
    if (prepurchasedVisualsTotal === PIPEDRIVE_INFINITY) {
      // Self-uploaded gallery
      if (product?.isAiPhotoEditing) {
        selectVisualsMutation.mutate(
          {
            assignmentId,
            workspaceId: userPropertyWorkspaceId,
            isClient: roles.isClient,
            filenames: Array.from(selected),
            product: assignmentProduct,
          },
          {
            onSuccess: (_, { assignmentId }) => {
              spawnSuccessToast(t('visuals_selected_success'))
              invalidateListVisuals(queryClient, assignmentId)
            }
          }
        )
        return
      } else if (
        !purchasedVisualsExist &&
        (await showConfirm(<Trans t={t} i18nKey="select_visuals_confirm_prompt_unlimited_selection">&nbsp;</Trans>, { title: t('select_visuals_confirm_title') }))
      ) {
        selectVisualsMutation.mutate(
          {
            assignmentId,
            workspaceId: userPropertyWorkspaceId,
            isClient: roles.isClient,
            filenames: allVisualsFilenames,
            product: assignmentProduct,
          },
          {
            onSuccess: (_, { assignmentId }) => {
              spawnSuccessToast(t('visuals_selected_success'))
              invalidateListVisuals(queryClient, assignmentId)
            }
          }
        )
        return
      }

      return
    }

    // Payment is necessary
    if (selectedPaidVisualsCount > 0) {
      setIsAdditionalVisualsPurchaseModalOpen(true)
      return
    }

    // No payment necessary
    if (selectedFreeVisualsCount > 0 && selectedPaidVisualsCount === 0) {

      if (!(await showConfirm(
        <Trans
          t={t}
          i18nKey={selectedFreeVisualsCount < prepurchasedVisualsRemaining ? 'select_visuals_confirm_prompt_free_remaining' : 'select_visuals_confirm_prompt'}
          values={{ remaining: prepurchasedVisualsRemaining - selectedFreeVisualsCount }}
        >
          &nbsp;
        </Trans>,
        {
          title: t('select_visuals_confirm_title')
        }
      ))) return

      selectVisualsMutation.mutate(
        {
          assignmentId,
          workspaceId: userPropertyWorkspaceId,
          isClient: roles.isClient,
          filenames: Array.from(selected),
          product: assignmentProduct,
        },
        {
          onSuccess: (_, { assignmentId }) => {
            spawnSuccessToast(t('visuals_selected_success'))
            invalidateListVisuals(queryClient, assignmentId)
          }
        }
      )
    }

  }, [product, selectedFreeVisualsCount, selectedPaidVisualsCount, logGalleryEvent, dealData?.customer.id, dealData?.customer.email, selectedNotPurchasedVisualsCount, prepurchasedVisualsTotal, allVisualsFilenames, selectedNotPurchasedVisualsKeys, isSelfUploadProduct, purchasedVisualsExist, showConfirm, spawnErrorToast, t, selectVisualsMutation, assignmentId, userPropertyWorkspaceId, roles.isClient, selected, spawnSuccessToast, queryClient, setIsAdditionalVisualsPurchaseModalOpen, prepurchasedVisualsRemaining, blurPartners])

  const additionalVisualsPurchase = useCallback((type: CurrentActionType) => {
    setCurrentActionType(type)
    initiateArchiveDownload()
  }, [initiateArchiveDownload, setCurrentActionType])

  const handleDownloadVisualsAnalytics = useCallback(() => {
    if (isPropertyPathname) {
      logAnalyticsEvent(AnalyticsEvent.PROPERTY_DOWNLOAD_VISUALS, {
        propertyName: propertyData?.title,
        propertyId: propertyData?.id,
        realtyId: propertyData?.id,
        propertyType: propertyData?.propertyType,
        planId: currentUserWorkspace?.id,
        productKind: product?.kind,
        productCategory: product?.category,
        visualIds: Array.from(selected),
      })
    } else {
      logGalleryEvent(AnalyticsEvent.CLICK_DOWNLOAD_VISUALS)
    }
  }, [isPropertyPathname, logGalleryEvent, product?.category, product?.kind, propertyData?.id, propertyData?.propertyType, propertyData?.title, selected, currentUserWorkspace?.id])

  const handleDownload = useCallback(() => {
    handleDownloadVisualsAnalytics()
    additionalVisualsPurchase(CurrentActionType.DOWNLOAD)
  }, [additionalVisualsPurchase, handleDownloadVisualsAnalytics])

  const onCancelToolBar = useCallback(() => {
    if (!isPropertyPathname) setIsToolBarOpen(false)
    deselectAll()
  }, [deselectAll, isPropertyPathname, setIsToolBarOpen])

  const handleExportingOptions = useCallback(() => {
    if (!hasWorkspaceActiveSubscription) {
      logGalleryEvent(AnalyticsEvent.GALLERY_SAAS_UPSELL_EDIT_VISUALS)

      return externalNavigate(getPricingUrl(i18n.language))
    }

    if (selectedAdditionalVisualsCount > 0) return additionalVisualsPurchase(CurrentActionType.EDITING)

    logGalleryEvent(AnalyticsEvent.GALLERY_FLOATING_BAR_EXPORT_OPTIONS)

    if (isPropertyPathname) {
      if (id && assignmentId) {
        navigate(Path.PROPERTY_ASSIGNMENT_EXPORTING_OPTIONS
          .replace(':id', id.toString())
          .replace(':assignmentId', assignmentId.toString()),
          { state: { selectedVisuals: selected } })
      }

    } else {
      navigate(Path.GALLERY_VISUALS_EDIT.replace(':id', assignmentId.toString()))
    }

  }, [additionalVisualsPurchase, assignmentId, hasWorkspaceActiveSubscription, id, isPropertyPathname, logGalleryEvent, navigate, selected, selectedAdditionalVisualsCount])

  useEffect(() => {
    if (purchasedVisualsExist && isPropertyPathname) setIsToolBarOpen(true)
  }, [isPropertyPathname, purchasedVisualsExist, setIsToolBarOpen])

  return (
    <Fragment>

      <GalleryFloatingToolBar
        isToolBarOpen={isToolBarOpen && mode === ToolbarMode.EXPORT_DOWNLOAD}
        toolBarLabel={
          <Fragment>
            {selectedPurchasedVisualsCount} <Box fontWeight={400} display='inline'> {t('selected')}</Box>
          </Fragment>
        }
        openToolBarButtonLabel={t('select_visuals')}
        onOpenToolBar={() => setIsToolBarOpen(true)}
        contentActionContainer={
          <Fragment>
            <MUIButton
              type="darkSecondaryNoborder"
              onClick={onCancelToolBar}
            >
              {t('cancel')}
            </MUIButton>
            {Array.from(purchasedVisualsKeys).every((key) => selected.has(key)) // Typescript v5 supports Set.isSupersetOf / isSubsetOf, however we cannot upgrade to typescript >= 5 since CRA scripts do not support it
              ? (
                <MUIButton
                  type="darkSecondaryNoborder"
                  onClick={deselectAll}
                >
                  {t('unselect_all')}
                </MUIButton>
              )
              : (
                <MUIButton
                  type="darkSecondaryNoborder"
                  onClick={selectAllPurchased}
                >
                  {t('select_all')}
                </MUIButton>
              )
            }
          </Fragment>
        }
        outputActionContainer={
          <Fragment>
            {!isVideo &&
              <MUITooltip content={!hasWorkspaceActiveSubscription ? t('link_copy_sass_upgrade') : undefined}>
                <MUIButton
                  type="darkSecondaryNoborder"
                  disabled={isVisualsEditingDisabled}
                  onClick={handleExportingOptions}
                >
                  {t('exporting_options')}
                </MUIButton>
              </MUITooltip>
            }
            <MUIButton
              type="darkPrimary"
              onClick={handleDownload}
              disabled={!selected.size}
              startIcon={<FileDownloadOutlinedIcon fontSize='large' />}
            >
              {t('direct_download')}
            </MUIButton>
          </Fragment>
        }
      />

      <GalleryFloatingToolBar
        isToolBarOpen={isToolBarOpen && mode === ToolbarMode.SELECTION}
        toolBarLabel={selectionModeLabel}
        openToolBarButtonLabel={t('select_visuals')}
        onOpenToolBar={() => setIsToolBarOpen(true)}
        contentActionContainer={
          <Fragment>

            <MUIButton
              type="darkSecondaryNoborder"
              onClick={onCancelToolBar}
            >
              {t('cancel')}
            </MUIButton>

            {!allowSelectAllButton && Array.from(unPurchasedVisualsKeys).some((key) => selected.has(key)) &&
              <MUIButton
                type="darkSecondaryNoborder"
                onClick={deselectAll}
              >
                {t('unselect_all')}
              </MUIButton>
            }

            {allowSelectAllButton &&
              <>
                {Array.from(unPurchasedVisualsKeys).every((key) => selected.has(key)) // Typescript v5 supports Set.isSupersetOf / isSubsetOf, however we cannot upgrade to typescript >= 5 since CRA scripts do not support it
                  ? (
                    <MUIButton
                      type="darkSecondaryNoborder"
                      onClick={deselectAll}
                    >
                      {t('unselect_all')}
                    </MUIButton>
                  )
                  : (
                    <MUIButton
                      type="darkSecondaryNoborder"
                      onClick={selectAllUnpurchased}
                    >
                      {t('select_all')}
                    </MUIButton>
                  )
                }
              </>
            }

          </Fragment>
        }
        outputActionContainer={
          <MUITooltip content={isPayAndAddDisabled ? t('cannot_order_pay_and_add_tooltip') : undefined}>
            <MUIButton
              fullWidth
              type="darkPrimary"
              disabled={!selected.size || isPayAndAddDisabled}
              onClick={() => handleVisualsSelection()}
              startIcon={selectedPaidVisualsCount > 0 ? <AddCard fontSize="large" /> : <AddPhotoAlternate fontSize="large" />}
            >
              {!purchasedVisualsExist
                ? t('confirm_selection')
                : (prepurchasedVisualsRemaining === 0 || selectedPaidVisualsCount > 0)
                  ? t('pay_and_add_to_purchased')
                  : t('add_to_purchased')
              }
            </MUIButton>
          </MUITooltip>
        }
      />

    </Fragment>
  )
}
